import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/Layout/MainLayout.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Label } from '../components/ui/label.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog.jsx';
import { Textarea } from '../components/ui/textarea.jsx';
import { Briefcase, Plus, Search, Pencil, Trash2, Upload, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const NIVEIS = ['junior', 'pleno', 'senior', 'gerente', 'diretor'];

const empty = { nome: '', descricao_sumaria: '', familia: '', nivel: '', setor: '', cbo: '' };

const CargosPadrao = () => {
  const userRole = localStorage.getItem('userRole') || 'respondent';
  const userEmail = (typeof window !== 'undefined' ? localStorage.getItem('userEmail') : '') || '';
  const isG2Admin = userEmail.toLowerCase() === 'g2consultoriarh@gmail.com';
  const canEdit = userRole === 'admin' || userRole === 'manager';

  const [items, setItems] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [setores, setSetores] = useState([]);
  const [q, setQ] = useState('');
  const [familiaFilter, setFamiliaFilter] = useState('');
  const [setorFilter, setSetorFilter] = useState('');
  const [editing, setEditing] = useState(null); // null | {} (novo) | existing doc
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkJson, setBulkJson] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (familiaFilter) params.set('familia', familiaFilter);
      if (setorFilter) params.set('setor', setorFilter);
      const { data } = await axios.get(`${API}/cargos-catalogo?${params}`, auth());
      setItems(data.items || []);
      setFamilias(data.familias || []);
      setSetores(data.setores || []);
    } catch (e) { console.error(e); }
  }, [q, familiaFilter, setorFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing({}); setForm(empty); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      nome: c.nome || '',
      descricao_sumaria: c.descricao_sumaria || '',
      familia: c.familia || '',
      nivel: c.nivel || '',
      setor: c.setor || '',
      cbo: c.cbo || '',
    });
  };
  const closeEdit = () => { setEditing(null); setForm(empty); };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.nome.trim()) { toast.error('Nome obrigatório'); return; }
    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        descricao_sumaria: form.descricao_sumaria || '',
        familia: form.familia || null,
        nivel: form.nivel || null,
        setor: form.setor || null,
        cbo: form.cbo || null,
      };
      if (editing && editing.id) {
        await axios.patch(`${API}/cargos-catalogo/${editing.id}`, payload, auth());
        toast.success('Cargo atualizado');
      } else {
        const { data } = await axios.post(`${API}/cargos-catalogo`, payload, auth());
        toast.success(data.already_existed ? 'Já existia — complementado' : 'Cargo criado');
      }
      closeEdit();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Remover "${c.nome}" do catálogo? Pesquisas existentes não serão afetadas.`)) return;
    try {
      await axios.delete(`${API}/cargos-catalogo/${c.id}`, auth());
      toast.success('Cargo removido');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao remover');
    }
  };

  const handleBulkImport = async () => {
    let parsed;
    try { parsed = JSON.parse(bulkJson); }
    catch { toast.error('JSON inválido'); return; }
    if (!Array.isArray(parsed)) { toast.error('JSON deve ser um array de cargos'); return; }
    setBulkLoading(true);
    try {
      const { data } = await axios.post(`${API}/cargos-catalogo/bulk-import`,
        { items: parsed, skip_duplicates: true }, auth());
      toast.success(`Importação: ${data.created} criados, ${data.updated} complementados, ${data.skipped} ignorados`);
      setBulkOpen(false); setBulkJson('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na importação');
    } finally { setBulkLoading(false); }
  };

  const handleExport = () => {
    const exportData = items.map(({ id, nome_key, usage_count, created_by, created_at, last_used_at, ...rest }) => rest);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `cargos_catalogo_${new Date().toISOString().slice(0,10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  };

  const exampleJson = `[
  {"nome": "Operador de Máquinas", "setor": "Industrial", "cbo": "7841-15", "familia": "Operacional", "nivel": "junior"},
  {"nome": "Analista Financeiro", "setor": "Financeiro", "cbo": "2522-10", "familia": "Administrativo", "nivel": "pleno", "descricao_sumaria": "Analisa fluxo de caixa e demonstrativos"}
]`;

  return (
    <MainLayout title="Cargos Padrão" userRole={userRole}>
      <div className="space-y-5" data-testid="cargos-padrao-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm text-slate-500">Catálogo compartilhado entre todas as pesquisas. {items.length} cargos.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn"><Download className="w-3.5 h-3.5 mr-1" /> Exportar JSON</Button>
            {canEdit && (
              <>
                <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-testid="bulk-import-btn"><Upload className="w-3.5 h-3.5 mr-1" /> Importar em lote</Button>
                <Button size="sm" onClick={openCreate} data-testid="new-cargo-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Novo cargo</Button>
              </>
            )}
          </div>
        </div>

        {/* Filtros */}
        <Card>
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs">Buscar (nome ou CBO)</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Ex: analista, 2524" className="h-9 pl-7 text-sm" data-testid="search-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Setor</Label>
              <Select value={setorFilter || 'all'} onValueChange={v => setSetorFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="filter-setor"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os setores</SelectItem>
                  {setores.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Família</Label>
              <Select value={familiaFilter || 'all'} onValueChange={v => setFamiliaFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="filter-familia"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as famílias</SelectItem>
                  {familias.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-slate-400">{items.length} resultado(s)</div>
          </CardContent>
        </Card>

        {/* Tabela */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Briefcase className="w-4 h-4" /> Catálogo</CardTitle></CardHeader>
          <CardContent>
            {items.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Nenhum cargo encontrado.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-3 py-2 font-medium text-slate-700">Cargo</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-700">Setor</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-700">CBO</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-700">Família</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-700">Nível</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-700">Uso</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-700"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(c => (
                      <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cargo-row-${c.id}`}>
                        <td className="px-3 py-2">
                          <p className="font-medium text-slate-900">{c.nome}</p>
                          {c.descricao_sumaria && <p className="text-[11px] text-slate-500 truncate max-w-[280px]">{c.descricao_sumaria}</p>}
                        </td>
                        <td className="px-3 py-2 text-slate-700">{c.setor || '—'}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">{c.cbo || '—'}</td>
                        <td className="px-3 py-2 text-slate-700">{c.familia || '—'}</td>
                        <td className="px-3 py-2 text-slate-700 capitalize">{c.nivel || '—'}</td>
                        <td className="px-3 py-2 text-right">
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{c.usage_count}x</span>
                        </td>
                        <td className="px-3 py-2 text-right space-x-1">
                          {canEdit && (
                            <button onClick={() => openEdit(c)} className="text-slate-400 hover:text-blue-600" data-testid={`edit-${c.id}`} title="Editar"><Pencil className="w-3.5 h-3.5" /></button>
                          )}
                          {isG2Admin && (
                            <button onClick={() => handleDelete(c)} className="text-slate-400 hover:text-red-600" data-testid={`delete-${c.id}`} title="Remover"><Trash2 className="w-3.5 h-3.5" /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal Editar / Criar */}
        <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
          <DialogContent className="max-w-lg" data-testid="cargo-modal">
            <DialogHeader>
              <DialogTitle>{editing?.id ? `Editar: ${editing.nome}` : 'Novo Cargo Padrão'}</DialogTitle>
              <DialogDescription className="text-xs">Cargos cadastrados ficam disponíveis para todas as pesquisas.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <Label className="text-xs">Nome do Cargo *</Label>
                <Input value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} required placeholder="Ex: Analista de RH Pleno" data-testid="form-nome" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Setor</Label>
                  <Input value={form.setor} onChange={e => setForm(p => ({ ...p, setor: e.target.value }))} placeholder="Ex: Industrial, RH" data-testid="form-setor" />
                </div>
                <div>
                  <Label className="text-xs">CBO <span className="text-slate-400 font-normal">(Classificação Brasileira de Ocupações)</span></Label>
                  <Input value={form.cbo} onChange={e => setForm(p => ({ ...p, cbo: e.target.value }))} placeholder="Ex: 2524-05" maxLength={10} data-testid="form-cbo" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Família</Label>
                  <Input value={form.familia} onChange={e => setForm(p => ({ ...p, familia: e.target.value }))} placeholder="Ex: Operacional, Tecnologia" data-testid="form-familia" />
                </div>
                <div>
                  <Label className="text-xs">Nível</Label>
                  <Select value={form.nivel || 'none'} onValueChange={v => setForm(p => ({ ...p, nivel: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="form-nivel"><SelectValue placeholder="Nenhum" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Nenhum —</SelectItem>
                      {NIVEIS.map(n => <SelectItem key={n} value={n}>{n.charAt(0).toUpperCase() + n.slice(1)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Descrição Sumária</Label>
                <Textarea value={form.descricao_sumaria} onChange={e => setForm(p => ({ ...p, descricao_sumaria: e.target.value }))} placeholder="Breve descrição das atribuições..." rows={3} data-testid="form-descricao" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeEdit}>Cancelar</Button>
                <Button type="submit" disabled={saving} data-testid="form-submit">{saving ? 'Salvando...' : (editing?.id ? 'Salvar alterações' : 'Criar cargo')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Bulk Import */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="max-w-2xl" data-testid="bulk-modal">
            <DialogHeader>
              <DialogTitle>Importar Cargos em Lote</DialogTitle>
              <DialogDescription className="text-xs">Cole um array JSON com os cargos. Cargos duplicados (mesmo nome) são ignorados ou complementados.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="bg-slate-50 border border-slate-200 rounded p-3 text-[11px] text-slate-600">
                <p className="font-semibold mb-1">Formato esperado:</p>
                <pre className="overflow-x-auto whitespace-pre-wrap">{exampleJson}</pre>
              </div>
              <Textarea
                value={bulkJson}
                onChange={e => setBulkJson(e.target.value)}
                rows={10}
                className="font-mono text-xs"
                placeholder="Cole o JSON aqui..."
                data-testid="bulk-textarea"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancelar</Button>
                <Button onClick={handleBulkImport} disabled={bulkLoading || !bulkJson.trim()} data-testid="bulk-submit">
                  {bulkLoading ? 'Importando...' : 'Importar'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default CargosPadrao;
